import {
  changePasswordByTokenApi,
  checkAccountByEmailApi,
  loginByEmailApi,
  loginForUser,
  logoutApi,
  registerApi,
  sendVerificationCodeApi,
} from '@api/auth.api';
import { getAllWorkspacesShortApi } from '@api/userOrganization.api';

import { AuthStep as AUTH_STEP, CodeTypeMap } from 'respona_api/generated/common_pb';
import { LoginResponseType, VerificationEmailResponseType } from '@ts/auth.types';
import { AuthHandlerTypeMap, VerificationCodeTypeMap } from 'respona_api/generated/auth-shared_pb';

import { shutdown as shutdownIntercom } from 'src/libs/intercom';
import urls from '@constants/urls';
import { calendlyStatusTypes } from '@constants/onboardingPopupStatuses';
import gtm from 'src/libs/gtm';
import { redirectUserTo } from '@utils/historyHandler';
import fetchMetadata from '@api/base/fetchMetadata';
import {
  getItemFromStorage,
  removeItemFromStorage,
  setItemInStorage,
} from '@utils/localStorageHandler';
import { isStandaloneMode } from '@constants/app-modes';
import * as common_pb from 'respona_api/generated/common_pb';
import { isConciergeRealm, isPublisherRealm } from '@utils/realmIdentifier';

class AuthService {
  // The login/register response can arrive with `workspaceId = 0` — a proto3 unset
  // default seen for AppSumo first-login registrations. Redirecting straight to
  // `/workspaces/0/...` produces a broken URL that never resolves a workspace.
  // Fall back to the user's favorite (or first) workspace before building the URL.
  // eslint-disable-next-line class-methods-use-this
  private async resolveWorkspaceId(candidate: number): Promise<number> {
    if (candidate && candidate > 0) {
      return candidate;
    }
    try {
      const workspaces = await getAllWorkspacesShortApi();
      const favorite = workspaces.find((w) => w.favorite);
      return favorite?.id ?? workspaces[0]?.id ?? candidate;
    } catch {
      return candidate;
    }
  }

  async loginCallback(
    history: History,
    response: LoginResponseType,
    isInvitation = false
  ): Promise<LoginResponseType> {
    const { sessionToken } = response;

    if (isStandaloneMode()) {
      setItemInStorage('token', sessionToken);
      if (response.firstLogin) {
        gtm.signUp({ userId: response.userId, email: response.userEmail });
      }
      gtm.signIn({ userId: response.userId, email: response.userEmail });
    }

    if (isConciergeRealm()) {
      if (response.firstLogin && !isInvitation) {
        // Business-email signups are prompted to schedule a demo call first;
        // everyone else goes straight to the referral-source questionary.
        redirectUserTo(
          history,
          response.businessEmail
            ? urls.ONBOARDING_SCHEDULE_CALL
            : urls.ONBOARDING_FIRST_ORDER_TRANSITION
        );
        return response;
      }
      redirectUserTo(history, urls.ORDERS(await this.resolveWorkspaceId(response.workspaceId)));
    } else if (isPublisherRealm()) {
      redirectUserTo(history, urls.WEBSITES(await this.resolveWorkspaceId(response.workspaceId)));
    } else if (response.authStep === AUTH_STEP.CAMPAIGN) {
      redirectUserTo(
        history,
        urls.CREATE_NEW_CAMPAIGN(await this.resolveWorkspaceId(response.workspaceId))
      );
    } else if (response.authStep === AUTH_STEP.DASHBOARD) {
      if (isStandaloneMode()) redirectUserTo(history, urls.DASHBOARD);
      else
        redirectUserTo(
          history,
          urls.ALL_CAMPAIGNS(await this.resolveWorkspaceId(response.workspaceId))
        );
    } else {
      redirectUserTo(history, '/');
    }

    if (isStandaloneMode()) {
      if (
        response.firstLogin &&
        getItemFromStorage('showCalendly') !== calendlyStatusTypes.COMPLETE
      ) {
        localStorage.setItem('showCalendly', calendlyStatusTypes.AWAITING);
      }
    }

    return response;
  }

  async checkAccountByEmail(email: string): Promise<VerificationEmailResponseType> {
    return checkAccountByEmailApi(email);
  }

  async sendVerificationCode(
    email: string,
    type: VerificationCodeTypeMap[keyof VerificationCodeTypeMap]
  ): Promise<void> {
    return sendVerificationCodeApi(email, type);
  }

  async login(
    history: History,
    email: string,
    password: string,
    captcha: string
  ): Promise<LoginResponseType> {
    const loginResponse = await loginByEmailApi(email, password, captcha);
    // Await the callback so callers that `await authService.login(...)` don't see
    // a redirect race after loginCallback became async (workspaceId fallback fetch).
    await this.loginCallback(history, loginResponse);
    return loginResponse;
  }

  async loginWithToken(
    history: History,
    token: string,
    type: AuthHandlerTypeMap[keyof AuthHandlerTypeMap],
    realm: common_pb.RealmTypeMap[keyof common_pb.RealmTypeMap],
    isInvitation = false
  ): Promise<LoginResponseType> {
    if (isStandaloneMode()) {
      const existingToken = localStorage.getItem('token');
      if (existingToken?.length > 0) {
        await logoutApi(existingToken);
        await removeItemFromStorage('token');
      }
    }

    return loginForUser(token, realm, type).then((response) =>
      this.loginCallback(history, response, isInvitation)
    );
  }

  async changePasswordByToken(
    history: History,
    token: string,
    email: string,
    password: string
  ): Promise<LoginResponseType> {
    return changePasswordByTokenApi(token, email, password).then((response) =>
      this.loginCallback(history, response)
    );
  }

  async register(
    email: string,
    password: string,
    fullName: string,
    captcha: string,
    code: string,
    refCodes: { key: string; type: CodeTypeMap[keyof CodeTypeMap] }[],
    source?: string
  ): Promise<LoginResponseType> {
    return registerApi(email, password, fullName, captcha, code, refCodes, source);
  }

  async logout(history?: History): Promise<void> {
    try {
      const { Authorization: token } = await fetchMetadata();
      if (token?.length > 0) {
        await logoutApi(token);
      }
    } catch (e) {
      console.error('Logout error', e);
    } finally {
      removeItemFromStorage('token');
      removeItemFromStorage('showCalendly');
      removeItemFromStorage('showEwebinar');
      if (isStandaloneMode()) {
        shutdownIntercom();
        redirectUserTo(history, urls.LOGIN);
      } else {
        redirectUserTo(history, '/');
      }
    }
  }
}

export const authService = new AuthService();
